import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Filter,
  CheckCircle2,
  Clock,
  ArrowRight,
  ExternalLink,
  HelpCircle,
  Sparkles,
  Layers,
  FileText,
  X,
  PhoneCall,
} from 'lucide-react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import type { Service, ServiceCategory } from '../../types/database';

export default function ServicesPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  useEffect(() => {
    fetchServicesData();
  }, []);

  const fetchServicesData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch categories
      const { data: catData, error: catErr } = await supabase
        .from('service_categories')
        .select('*')
        .order('display_order', { ascending: true });

      if (catErr) console.error('[SERVICES] Error fetching categories:', catErr);
      if (catData) setCategories(catData as ServiceCategory[]);

      // 2. Fetch active services
      const { data: srvData, error: srvErr } = await supabase
        .from('services')
        .select('*, category:service_categories(*)')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (srvErr) {
        console.error('[SERVICES] Error fetching services:', srvErr);
        setError('Failed to load services.');
      }
      if (srvData) setServices(srvData as Service[]);
    } catch (err) {
      console.error('[SERVICES] Unexpected error fetching services:', err);
      setError('Unable to load services catalog. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (slug: string) => {
    setSelectedCategory(slug);
    if (slug === 'all') {
      searchParams.delete('category');
      setSearchParams(searchParams);
    } else {
      setSearchParams({ category: slug });
    }
  };

  // Filter services by category and search
  const filteredServices = services.filter((srv) => {
    const matchesCategory =
      selectedCategory === 'all' || srv.category?.slug === selectedCategory;

    const query = searchQuery.toLowerCase();
    const matchesSearch =
      !query ||
      srv.title.toLowerCase().includes(query) ||
      srv.description.toLowerCase().includes(query) ||
      srv.category?.name.toLowerCase().includes(query) ||
      srv.deliverables?.some((d) => d.toLowerCase().includes(query)) ||
      srv.features?.some((f) => f.toLowerCase().includes(query));

    return matchesCategory && matchesSearch;
  });

  const getServiceTypeBadge = (srv: Service) => {
    if (srv.category?.slug === 'training-learning' || srv.slug.includes('courses')) {
      return { label: 'Training', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    if (srv.pricing_model === 'Subscription' || srv.pricing_model === 'Monthly Retainer') {
      return { label: 'Recurring', color: 'bg-purple-50 text-purple-700 border-purple-200' };
    }
    if (srv.pricing_model === 'Custom Quote' || srv.base_price === 0) {
      return { label: 'Custom Quote', color: 'bg-amber-50 text-amber-700 border-amber-200' };
    }
    return { label: 'One-Time Delivery', color: 'bg-blue-50 text-blue-700 border-blue-200' };
  };

  return (
    <div className="font-sans min-h-screen bg-brokenWhite-100 flex flex-col">
      <Navbar />

      {/* Hero / Page Header */}
      <section className="pt-28 pb-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-4 font-semibold">
            <Link to="/" className="hover:text-clairBlue-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-darkBlue-900">Official Services Catalog</span>
          </nav>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className="inline-block px-3 py-1 bg-clairBlue-50 text-clairBlue-600 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
                BientoMart Digital Solutions
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-darkBlue-900 tracking-tight leading-tight">
                Digital Services & Business Engineering
              </h1>
              <p className="text-gray-500 text-sm sm:text-base mt-3 leading-relaxed">
                From website design and systems development to practical AI implementation, digital marketing, and team training across Uganda and East Africa.
              </p>
            </div>

            {/* Direct Request CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/portal/request-service"
                className="px-6 py-3.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white font-bold text-xs sm:text-sm rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all hover:scale-105"
              >
                <span>Request Custom Solution</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/256773515447"
                target="_blank"
                rel="noreferrer"
                className="px-5 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                <span>Direct WhatsApp Chat</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filter and Search Bar */}
      <section className="sticky top-16 lg:top-20 z-30 bg-white/95 backdrop-blur-md border-b border-gray-200 py-3 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Category Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              <button
                onClick={() => handleCategorySelect('all')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === 'all'
                    ? 'bg-darkBlue-900 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Services ({services.length})
              </button>

              {categories.map((cat) => {
                const isSelected = selectedCategory === cat.slug;
                const count = services.filter((s) => s.category_id === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.slug)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-darkBlue-900 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-72 flex-shrink-0">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search services, deliverables..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-darkBlue-900 focus:outline-none focus:ring-2 focus:ring-clairBlue-500 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Services Grid */}
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-10 flex-1 w-full">
        {loading ? (
          <div className="text-center py-24">
            <div className="w-12 h-12 border-4 border-clairBlue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-xs text-gray-400 font-semibold">Loading official services catalog...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-red-100 p-8 shadow-sm max-w-md mx-auto">
            <HelpCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-800">Unable to Load Services</h3>
            <p className="text-xs text-gray-500 mt-1 mb-6">{error}</p>
            <button
              onClick={fetchServicesData}
              className="px-5 py-2.5 bg-darkBlue-900 text-white rounded-xl text-xs font-bold hover:bg-darkBlue-800"
            >
              Try Again
            </button>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-200 p-8 shadow-sm">
            <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-800">No services match your filter</h3>
            <p className="text-xs text-gray-500 mt-1 mb-6">
              Try adjusting your search terms or view our full catalog.
            </p>
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
              }}
              className="px-5 py-2.5 bg-darkBlue-900 text-white rounded-xl text-xs font-bold"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((srv) => {
              const typeBadge = getServiceTypeBadge(srv);
              const isCustomQuote = srv.pricing_model === 'Custom Quote' || srv.base_price === 0;

              return (
                <div
                  key={srv.id}
                  className="bg-white rounded-3xl p-6 sm:p-7 border border-gray-200/80 shadow-sm flex flex-col justify-between hover:border-clairBlue-300 hover:shadow-md transition-all group"
                >
                  <div>
                    {/* Header Badges */}
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider line-clamp-1">
                        {srv.category?.name}
                      </span>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${typeBadge.color}`}>
                        {typeBadge.label}
                      </span>
                    </div>

                    {/* Title & Description */}
                    <h3 className="text-lg font-bold text-darkBlue-900 group-hover:text-clairBlue-600 transition-colors leading-snug mb-2">
                      {srv.title}
                    </h3>
                    <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed mb-4">
                      {srv.description}
                    </p>

                    {/* Deliverables Snippet */}
                    {srv.deliverables && srv.deliverables.length > 0 && (
                      <div className="space-y-1.5 mb-6 pt-3 border-t border-gray-100">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                          Key Deliverables:
                        </div>
                        {srv.deliverables.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex items-start gap-1.5 text-xs text-gray-700">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-1">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pricing & Actions Footer */}
                  <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
                    <div>
                      <span className="text-[10px] text-gray-400 block font-semibold">
                        {isCustomQuote ? 'Pricing Model' : 'Starting From'}
                      </span>
                      {isCustomQuote ? (
                        <span className="text-sm font-bold text-amber-600">
                          Request a Quote
                        </span>
                      ) : (
                        <span className="text-lg font-black text-darkBlue-900">
                          ${srv.base_price.toFixed(2)}{' '}
                          <span className="text-xs font-normal text-gray-400">
                            {srv.pricing_model === 'Monthly Retainer' ? '/ mo' : srv.currency}
                          </span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setSelectedService(srv)}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-darkBlue-900 rounded-xl text-xs font-bold transition-colors"
                      >
                        Details
                      </button>
                      <Link
                        to={`/portal/request-service?serviceId=${srv.id}`}
                        className="px-4 py-2 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1"
                      >
                        <span>Commission</span>
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Category 5 Spotlight: Request a Custom Solution Banner */}
        <section className="mt-14 bg-gradient-to-r from-darkBlue-900 to-darkBlue-800 rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden shadow-xl">
          <div className="relative z-10 max-w-2xl">
            <span className="px-3 py-1 bg-clairBlue-500/20 text-clairBlue-300 rounded-full text-xs font-bold uppercase tracking-wider inline-block mb-3">
              Category 5: Custom Digital Projects
            </span>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
              Have a Custom System or Unique Project Requirement?
            </h2>
            <p className="text-gray-300 text-xs sm:text-sm mt-3 leading-relaxed">
              We engineer custom administrative software, inventory and membership systems, multi-branch portals, and bespoke API workflows for schools, SACCOs, clinics, and enterprises.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/portal/request-service?serviceId=b1000000-0000-0000-0000-000000000015"
                className="px-6 py-3.5 bg-clairBlue-500 hover:bg-clairBlue-400 text-white font-bold text-xs sm:text-sm rounded-xl shadow-lg transition-all"
              >
                Submit Project Brief & Requirements
              </Link>
              <Link
                to="/academy"
                className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold text-xs sm:text-sm rounded-xl transition-all"
              >
                Explore BientoMart Academy
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Individual Service Detail Modal */}
      {selectedService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 sm:p-8 border border-gray-100 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-start justify-between pb-4 border-b border-gray-100 mb-5">
              <div>
                <span className="text-[10px] font-bold text-clairBlue-600 uppercase tracking-widest bg-clairBlue-50 px-2.5 py-1 rounded-full inline-block mb-2">
                  {selectedService.category?.name}
                </span>
                <h3 className="text-xl sm:text-2xl font-black text-darkBlue-900 leading-tight">
                  {selectedService.title}
                </h3>
              </div>
              <button
                onClick={() => setSelectedService(null)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="space-y-6 text-xs sm:text-sm">
              {/* Description */}
              <div>
                <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[11px] mb-1.5">
                  Service Description
                </h4>
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {selectedService.description}
                </p>
              </div>

              {/* Service Features */}
              {selectedService.features && selectedService.features.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[11px] mb-2">
                    Scope & Included Capabilities
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {selectedService.features.map((feat, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-gray-700 bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                        <CheckCircle2 className="w-4 h-4 text-clairBlue-500 flex-shrink-0 mt-0.5" />
                        <span className="text-xs">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deliverables */}
              {selectedService.deliverables && selectedService.deliverables.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[11px] mb-2">
                    Expected Handover Deliverables
                  </h4>
                  <div className="space-y-1.5 bg-emerald-50/60 p-4 rounded-2xl border border-emerald-100">
                    {selectedService.deliverables.map((deliv, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-emerald-950 font-semibold text-xs">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span>{deliv}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Box */}
              <div className="p-4 rounded-2xl bg-darkBlue-900 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <span className="text-[10px] text-clairBlue-300 font-bold uppercase tracking-wider block">
                    Pricing Structure ({selectedService.pricing_model})
                  </span>
                  <div className="text-xl sm:text-2xl font-black">
                    {selectedService.base_price > 0
                      ? `$${selectedService.base_price.toFixed(2)} ${selectedService.currency}`
                      : 'Custom Quotation Required'}
                  </div>
                  <span className="text-[11px] text-gray-300">
                    Payment via approved manual bank wire or mobile money
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Link
                    to={`/portal/request-service?serviceId=${selectedService.id}`}
                    onClick={() => setSelectedService(null)}
                    className="px-5 py-2.5 bg-clairBlue-500 hover:bg-clairBlue-400 text-white font-bold rounded-xl text-xs sm:text-sm shadow-md transition-all whitespace-nowrap"
                  >
                    Request This Service
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
